import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "./ui/button";
import { ArrowRight, Trophy, TrendingUp, Users, ShieldCheck } from "lucide-react";
import { siteConfig } from "../mock/mock";
import { fetchStats } from "../lib/api";

const DEFAULT_HERO = {
  yearsOfExpertise: 28,
  activeMembers: 675,
  averageWinRate: 78,
  leaguesCovered: "Almost all leagues covered",
};

const Hero = () => {
  const navigate = useNavigate();
  const [hero, setHero] = useState(DEFAULT_HERO);

  const load = async () => {
    try {
      const s = await fetchStats();
      if (s?.hero) setHero({ ...DEFAULT_HERO, ...s.hero });
    } catch {}
  };

  useEffect(() => {
    load();
    const handler = () => load();
    window.addEventListener("ep-data-updated", handler);
    const iv = setInterval(load, 20000);
    return () => {
      window.removeEventListener("ep-data-updated", handler);
      clearInterval(iv);
    };
  }, []);

  return (
    <section className="relative overflow-hidden bg-ep-royal">
      <div className="absolute -top-32 -left-24 w-[520px] h-[520px] rounded-full bg-[#1c3aa8] opacity-30 blur-3xl hero-orb" />
      <div className="absolute -bottom-32 -right-24 w-[560px] h-[560px] rounded-full bg-[#ff5a5a] opacity-15 blur-3xl hero-orb" />
      <div
        className="absolute inset-0 opacity-[0.06] pointer-events-none"
        style={{
          backgroundImage:
            "linear-gradient(rgba(255,255,255,0.08) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.08) 1px, transparent 1px)",
          backgroundSize: "48px 48px",
        }}
      />

      <div className="relative pt-4 pb-2 flex justify-center">
        <a
          href="https://explorerpicks.com/"
          target="_blank"
          rel="noopener"
          className="inline-block"
          aria-label="Explorer Picks — Football Predictions"
        >
          <img
            src="/explorer-picks-banner.gif"
            alt="Explorer Picks — Football Predictions"
            width="380"
            height="90"
            className="rounded-md shadow-xl block"
          />
        </a>
      </div>

      <div className="relative max-w-4xl mx-auto px-5 lg:px-8 pt-6 pb-24 lg:pt-8 lg:pb-32 text-center">
        <div className="fade-up">
          <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-white/8 border border-white/15 text-white/85 text-xs font-medium mb-7 mx-auto">
            <span className="w-1.5 h-1.5 rounded-full bg-[#ff5a5a] animate-pulse" />
            {siteConfig.hero.kicker}
          </div>

          <h1 className="text-white font-extrabold text-4xl sm:text-5xl lg:text-6xl xl:text-[68px] leading-[1.05] tracking-tight max-w-4xl mx-auto">
            {siteConfig.hero.title
              .split(".")
              .filter(Boolean)
              .map((chunk, i, arr) => (
                <span key={i} className="block">
                  {chunk.trim()}
                  {i < arr.length - 1 && <span className="text-[#ff5a5a]">.</span>}
                </span>
              ))}
          </h1>

          <p className="mt-7 text-white/75 text-base sm:text-lg max-w-2xl mx-auto leading-relaxed">
            {siteConfig.hero.subtitle}
          </p>

          <div className="mt-9 flex flex-wrap gap-3 justify-center">
            <Button
              onClick={() => navigate("/membership")}
              className="bg-ep-coral hover:bg-[#ff7676] text-white h-13 px-7 rounded-lg font-semibold coral-glow group"
              style={{ height: 52 }}
            >
              {siteConfig.hero.cta}
              <ArrowRight
                className="ml-2 transition-transform group-hover:translate-x-1"
                size={18}
              />
            </Button>
            <Button
              onClick={() => navigate("/archive")}
              variant="outline"
              className="bg-transparent hover:bg-white/10 border-white/25 text-white h-13 px-7 rounded-lg font-semibold"
              style={{ height: 52 }}
            >
              {siteConfig.hero.ctaSecondary}
            </Button>
          </div>

          <div className="mt-12 flex flex-wrap items-center justify-center gap-x-8 gap-y-3 text-white/75 text-sm">
            <div className="flex items-center gap-2">
              <Trophy size={16} className="text-[#ff5a5a]" />
              <span>{hero.yearsOfExpertise} years of expertise</span>
            </div>
            <div className="flex items-center gap-2">
              <Users size={16} className="text-[#ff5a5a]" />
              <span>{hero.activeMembers}+ active members</span>
            </div>
            <div className="flex items-center gap-2">
              <TrendingUp size={16} className="text-[#ff5a5a]" />
              <span>{hero.averageWinRate}% average win rate</span>
            </div>
            <div className="flex items-center gap-2">
              <ShieldCheck size={16} className="text-[#ff5a5a]" />
              <span>{hero.leaguesCovered}</span>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Hero;
